"use client";

import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { PenTool, CircleCheckBig } from "lucide-react";
import type { MutableRefObject } from "react";
import type { TradingChartRef } from "@/components/TradingChart/TradingChart";
import type {
  TrendLineData,
  RectangleData,
  LabelData,
} from "@/components/TradingChart/types";
import { apiClient } from "@/lib/api-client";
import { useAuth } from "@/contexts/AuthContext";
import { getViewModeChrome } from "../view-mode-chrome";

interface AddDrawingsSuccessProps {
  args?: any;
  result?: any;
  chartRef?: MutableRefObject<TradingChartRef | null>;
  viewMode?: string;
}

export function AddDrawingsSuccess({
  args,
  chartRef,
  viewMode,
}: AddDrawingsSuccessProps) {
  const chrome = getViewModeChrome(viewMode);
  const { user } = useAuth();
  const hasAddedDrawingsRef = useRef(false);

  useEffect(() => {
    const drawingSetId = args?.id;

    // Prevent double execution in React Strict Mode
    if (hasAddedDrawingsRef.current) return;

    const drawings: any[] = Array.isArray(args?.drawings) ? args.drawings : [];
    if (!chartRef?.current || drawings.length === 0) return;

    hasAddedDrawingsRef.current = true;

    const addDrawingsAndCapture = async () => {
      try {
        const addedIds: string[] = [];
        drawings.forEach((d, index) => {
          if (d.type === "trendline") {
            chartRef.current?.addTrendLine(d as TrendLineData);
            addedIds.push(d.id);
          } else if (d.type === "rectangle") {
            chartRef.current?.addRectangle(d as RectangleData);
            addedIds.push(d.id);
          } else if (d.type === "label") {
            chartRef.current?.addLabel(d as LabelData);
            addedIds.push(d.id);
          } else {
          }
        });
        // Only focus on drawings for authenticated users to prevent unwanted zoom behavior
        if (user) {
          chartRef.current?.focusOnDrawing(addedIds);
        }

        const screenshot = await chartRef.current?.captureBase64?.("image/png");
        const viewport = chartRef.current?.getViewport?.();
      } catch (error) {
      }
    };

    addDrawingsAndCapture();
  }, []);

  const drawings: any[] = Array.isArray(args?.drawings) ? args.drawings : [];
  const counts = drawings.reduce(
    (acc: any, d: any) => {
      if (d.type === "trendline") acc.tl++;
      else if (d.type === "rectangle") acc.rect++;
      else if (d.type === "label") acc.lbl++;
      return acc;
    },
    { tl: 0, rect: 0, lbl: 0 }
  );

  const title = "Drawings added";
  const subtitle = `Trendlines: ${counts.tl} · Zones: ${counts.rect} · Labels: ${counts.lbl}`;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        <div className="px-3 py-2 flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-2.5">
            <PenTool
                className={`w-3.5 h-3.5 ${chrome.iconMuted} shrink-0`}
                strokeWidth={2}
              />
            <div className="flex flex-col">
              <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                {title}
              </span>
              <span className={`text-[11px] ${chrome.subtext} truncate`}>
                {subtitle}
              </span>
            </div>
          </div>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
          >
            <CircleCheckBig className="h-4 w-4 text-green-500" />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
